import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Alert,
  Badge,
  Loader,
  Table,
  Code,
  Accordion,
  Modal,
  TextInput,
  Select,
  ScrollArea,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconArrowLeft,
  IconRefresh,
  IconFileTypePdf,
  IconFileTypeTxt,
  IconRepeat,
  IconPhoneCall,
  IconPlayerPlay,
  IconPlayerStopFilled
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams } from 'react-router-dom';
import { getReport, getReportPdf, getReportMarkdown } from '../api_scripts/reportsClient';
import { cancelRun, getRun, getTroubleshoot, startRun } from '../api_scripts/runsClient';
import { listAllowlist } from '../api_scripts/allowlistClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';

const STATUS_COLORS = { PASS: 'green', FAIL: 'red', SKIPPED: 'gray', ERROR: 'orange' };

function ReportViewer({ onBack }) {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Troubleshoot payload (recording presigned URL, transcript chunks, slim
  // step verdicts). Fetched in parallel with the report and rendered as the
  // "Troubleshoot" card below the verdict table.
  const [troubleshoot, setTroubleshoot] = useState(null);
  const audioRef = useRef(null);
  // Retry-modal state. Populated when the user clicks "Retry run".
  const [retryOpen, setRetryOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryRunRow, setRetryRunRow] = useState(null);
  const [retryDest, setRetryDest] = useState(null);
  const [retryFromDid, setRetryFromDid] = useState('');
  const [allowlist, setAllowlist] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  // Run row (from GET /runs/{id}) — used to decide whether Cancel is available.
  // report.result.status only reflects the finalized report; a PENDING run has
  // no result yet, so we lean on the raw run status.
  const [runStatus, setRunStatus] = useState(null);
  const backendConfigured = isSitBackendConfigured();

  const reload = useCallback(async () => {
    if (!backendConfigured || !runId) return;
    setLoading(true);
    setError(null);
    try {
      // Parallel: report.json (canonical verdicts + assembly) + troubleshoot
      // (presigned audio + transcript chunks) + run row (status for Cancel
      // button). Each fails independently — a successful report is the
      // higher-value payload; the troubleshoot panel just doesn't render
      // if it errors, and the Cancel button hides if the run row is stale.
      const [reportData, troubleshootData, runData] = await Promise.allSettled([
        getReport(runId),
        getTroubleshoot(runId),
        getRun(runId)
      ]);
      if (reportData.status === 'fulfilled') {
        setReport(reportData.value);
      } else {
        setError(reportData.reason?.response?.data?.message ?? reportData.reason?.message ?? 'Failed to load report.');
      }
      setTroubleshoot(troubleshootData.status === 'fulfilled' ? troubleshootData.value : null);
      setRunStatus(runData.status === 'fulfilled' ? (runData.value?.status ?? null) : null);
    } finally {
      setLoading(false);
    }
  }, [backendConfigured, runId]);

  const handleCancelRun = useCallback(async () => {
    if (!runId) return;
    if (!window.confirm(
      'Cancel this run?\n\n' +
      '• The state machine execution stops now.\n' +
      '• The in-flight PSTN call (if any) will hang up on the next Chime event.\n' +
      '• The report finalizes against whatever transcript accumulated so far.'
    )) return;
    setCancelling(true);
    try {
      const result = await cancelRun(runId);
      notifications.show({
        title: result.changed ? 'Run cancelled' : 'Run already terminal',
        message: result.changed
          ? `Status flipped to ${result.status}. Reload in a few seconds to see the finalized report.`
          : `Status was already ${result.status}. Nothing changed.`,
        color: result.changed ? 'orange' : 'yellow'
      });
      setRunStatus(result.status);
      // Give the reporter a couple of seconds to finalize, then refresh.
      setTimeout(reload, 3000);
    } catch (e) {
      notifications.show({
        title: 'Cancel failed',
        message: e?.response?.data?.message ?? e.message,
        color: 'red'
      });
    } finally {
      setCancelling(false);
    }
  }, [runId, reload]);

  useEffect(() => {
    reload();
  }, [reload]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if (!runId) return;
    try {
      const blob = await getReportPdf(runId);
      downloadBlob(blob, `sit-report-${runId}.pdf`);
    } catch (e) {
      notifications.show({
        title: 'PDF download failed',
        message: e?.response?.data?.message ?? e.message,
        color: 'red'
      });
    }
  };

  const openRetry = async () => {
    if (!runId) return;
    try {
      const [row, entries] = await Promise.all([
        getRun(runId),
        listAllowlist().catch(() => [])
      ]);
      if (!row?.plan) {
        notifications.show({
          title: 'Cannot retry',
          message: 'This run was created before plans were persisted. Re-author or re-import to retry.',
          color: 'yellow',
          autoClose: 8000
        });
        return;
      }
      setRetryRunRow(row);
      setRetryDest(row.destination ?? null);
      setRetryFromDid(row.fromDid ?? '');
      setAllowlist(entries.map((e) => ({ value: e.did, label: `${e.did} — ${e.owner}` })));
      setRetryOpen(true);
    } catch (e) {
      notifications.show({
        title: 'Failed to load run',
        message: e?.response?.data?.message ?? e.message,
        color: 'red'
      });
    }
  };

  const handleRetry = async () => {
    if (!retryRunRow?.plan || !retryDest) return;
    setRetrying(true);
    try {
      const plan = {
        ...retryRunRow.plan,
        target: { ...(retryRunRow.plan.target || {}), destination: retryDest, fromDid: retryFromDid }
      };
      const opts = { idempotencyKey: crypto.randomUUID() };
      if (retryRunRow.diagrams && retryRunRow.diagrams.length > 0) {
        opts.diagrams = retryRunRow.diagrams;
      }
      const result = await startRun(plan, opts);
      notifications.show({
        title: 'Retry started',
        message: `New testRunId: ${result.testRunId}`,
        color: 'green'
      });
      setRetryOpen(false);
      navigate(`/history/${result.testRunId}`);
    } catch (e) {
      notifications.show({
        title: 'Retry failed',
        message: e?.response?.data?.message ?? e.message,
        color: 'red',
        autoClose: 8000
      });
    } finally {
      setRetrying(false);
    }
  };

  const handleDownloadMarkdown = async () => {
    if (!runId) return;
    try {
      const md = await getReportMarkdown(runId);
      const blob = new Blob([md], { type: 'text/markdown' });
      downloadBlob(blob, `sit-report-${runId}.md`);
    } catch (e) {
      notifications.show({
        title: 'Markdown download failed',
        message: e?.response?.data?.message ?? e.message,
        color: 'red'
      });
    }
  };

  return (
    <Container size="lg" py="lg">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to History
        </Button>
        <Group>
          {runStatus && ['PENDING', 'DIALING', 'IN_PROGRESS'].includes(runStatus) && (
            <Button
              variant="light"
              color="orange"
              leftSection={<IconPlayerStopFilled size={16} />}
              onClick={handleCancelRun}
              loading={cancelling}
              disabled={!backendConfigured || cancelling}
            >
              Cancel run
            </Button>
          )}
          <Button
            variant="filled"
            color="violet"
            leftSection={<IconRepeat size={16} />}
            onClick={openRetry}
            disabled={!backendConfigured || !report}
          >
            Retry run
          </Button>
          <Button
            variant="light"
            color="violet"
            leftSection={<IconFileTypePdf size={16} />}
            onClick={handleDownloadPdf}
            disabled={!backendConfigured || !report}
          >
            Download PDF
          </Button>
          <Button
            variant="light"
            color="violet"
            leftSection={<IconFileTypeTxt size={16} />}
            onClick={handleDownloadMarkdown}
            disabled={!backendConfigured || !report}
          >
            Download Markdown
          </Button>
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={reload} disabled={!backendConfigured}>
            Refresh
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        <Title order={2}>Run Report</Title>
        <Text size="sm" color="dimmed">Run ID: <Code>{runId || '(none)'}</Code></Text>

        {!backendConfigured && <Alert color="yellow">Backend not configured.</Alert>}
        {error && <Alert color="red">{error}</Alert>}

        {loading && <Group justify="center" py="xl"><Loader /></Group>}

        {report && (
          <>
            <Card withBorder padding="lg" radius="md">
              <Stack gap="xs">
                <Group gap="sm">
                  <Title order={4}>Result:</Title>
                  <Badge size="lg" color={STATUS_COLORS[report.result?.status] ?? 'gray'}>
                    {report.result?.status ?? 'UNKNOWN'}
                  </Badge>
                  {report.result?.failedStepId && (
                    <Text size="sm" color="red">
                      Failed at <code>{report.result.failedStepId}</code>
                    </Text>
                  )}
                </Group>
                <Text size="sm" color="dimmed">
                  Plan: <code>{report.run?.planId}</code> &middot; CCaaS: <code>{report.run?.ccaas}</code>
                </Text>
                <Text size="sm" color="dimmed">
                  {report.run?.target?.fromDid} → {report.run?.target?.destination}
                </Text>
                <Text size="sm" color="dimmed">
                  Dialed at {report.run?.timeline?.dialedAt}
                  {report.run?.timeline?.durationSec
                    ? ` · duration ${report.run.timeline.durationSec.toFixed(1)}s`
                    : ''}
                </Text>
              </Stack>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Title order={4} mb="sm">Steps</Title>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Step</Table.Th>
                    <Table.Th style={{ width: 90 }}>Status</Table.Th>
                    <Table.Th>Matched by</Table.Th>
                    <Table.Th>Confidence</Table.Th>
                    <Table.Th>Reasoning</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(report.steps ?? []).map((s, i) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>{i + 1}</Table.Td>
                      <Table.Td><code>{s.id}</code></Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLORS[s.status] ?? 'gray'} style={{ minWidth: 64, whiteSpace: 'nowrap' }}>
                          {s.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{s.matchedBy ?? '—'}</Table.Td>
                      <Table.Td>{typeof s.confidence === 'number' ? s.confidence.toFixed(2) : '—'}</Table.Td>
                      <Table.Td><Text size="sm">{s.reasoning ?? ''}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Title order={4} mb="sm">CCaaS correlation</Title>
              {report.ccaasCorrelation?.matched ? (
                <Stack gap="xs">
                  <Text size="sm">
                    <strong>Connector:</strong> <code>{report.ccaasCorrelation.connector}</code>
                  </Text>
                  <Text size="sm">
                    <strong>External call id:</strong>{' '}
                    <code>{report.ccaasCorrelation.primary?.externalCallId}</code>
                  </Text>
                  {report.ccaasCorrelation.primary?.queue && (
                    <Text size="sm"><strong>Queue:</strong> {report.ccaasCorrelation.primary.queue}</Text>
                  )}
                  {report.ccaasCorrelation.primary?.agentId && (
                    <Text size="sm"><strong>Agent:</strong> {report.ccaasCorrelation.primary.agentId}</Text>
                  )}
                </Stack>
              ) : (
                <Stack gap="xs">
                  <Text size="sm">Not matched.</Text>
                  {(report.ccaasCorrelation?.notes ?? []).map((n, i) => (
                    <Text size="xs" color="dimmed" key={i}>· {n}</Text>
                  ))}
                </Stack>
              )}
            </Card>

            <TroubleshootCard
              troubleshoot={troubleshoot}
              audioRef={audioRef}
              fallbackUris={{ recordingUri: report.recordingUri, transcriptUri: report.transcriptUri }}
            />


            <Accordion variant="separated">
              <Accordion.Item value="raw">
                <Accordion.Control>Raw report JSON</Accordion.Control>
                <Accordion.Panel>
                  <Code block style={{ maxHeight: 360, overflow: 'auto' }}>
                    {JSON.stringify(report, null, 2)}
                  </Code>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </>
        )}
      </Stack>

      <Modal
        opened={retryOpen}
        onClose={() => !retrying && setRetryOpen(false)}
        title="Retry test run"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Re-runs the same plan
            {retryRunRow?.diagrams?.length > 0 ? ' (with the original source diagram)' : ''}
            {' '}as a new test run. Adjust the destination or harness DID below if you want
            to retarget the call.
          </Text>
          <Select
            label="Destination (allowlist)"
            placeholder="Pick an allowlisted DID"
            data={allowlist}
            value={retryDest}
            onChange={setRetryDest}
            searchable
            description={allowlist.length === 0 ? 'Allowlist is empty.' : null}
          />
          <TextInput
            label="From DID (harness)"
            value={retryFromDid}
            onChange={(e) => setRetryFromDid(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRetryOpen(false)} disabled={retrying}>
              Cancel
            </Button>
            <Button
              color="violet"
              leftSection={<IconPhoneCall size={14} />}
              onClick={handleRetry}
              loading={retrying}
              disabled={!retryDest}
            >
              Start retry
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

/**
 * Troubleshoot panel — HTML5 audio player for the call recording + clickable
 * transcript chunks that scrub the player to their start time. Renders only
 * when the troubleshoot endpoint returns data; falls back to showing the raw
 * S3 URIs from report.json when nothing landed (e.g., recording still in
 * the post-call delay window).
 */
function TroubleshootCard({ troubleshoot, audioRef, fallbackUris }) {
  const recordingUrl = troubleshoot?.recordingUrl ?? null;
  const transcriptChunks = troubleshoot?.transcriptChunks ?? [];
  const aiSummary = troubleshoot?.aiSummary ?? null;

  const seekTo = (chunk) => {
    const ms = typeof chunk?.startMs === 'number' ? chunk.startMs : null;
    if (ms === null || !audioRef.current) return;
    audioRef.current.currentTime = ms / 1000;
    audioRef.current.play().catch(() => {});
  };

  const fmtTime = (ms) => {
    if (typeof ms !== 'number') return '—';
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const speakerColor = (sp) =>
    sp === 'CUSTOMER' ? 'violet' : sp === 'AGENT' ? 'blue' : 'gray';

  const hasAnything = recordingUrl || transcriptChunks.length > 0;
  if (!hasAnything) {
    // Recording / transcript haven't landed yet — surface the raw S3 URIs
    // from report.json if available so the engineer at least knows where
    // they will appear once finalization completes.
    if (!fallbackUris?.recordingUri && !fallbackUris?.transcriptUri) return null;
    return (
      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Troubleshoot</Title>
        <Alert color="yellow" variant="light">
          Recording / transcript not yet available. Reporter polls S3 for up to 11 min
          after the call hangs up.
        </Alert>
        <Stack gap="xs" mt="sm">
          {fallbackUris?.recordingUri && (
            <Text size="sm">Recording (pending): <Code>{fallbackUris.recordingUri}</Code></Text>
          )}
          {fallbackUris?.transcriptUri && (
            <Text size="sm">Transcript (pending): <Code>{fallbackUris.transcriptUri}</Code></Text>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Title order={4} mb="sm">Troubleshoot</Title>
      {recordingUrl ? (
        <Stack gap="xs" mb="sm">
          <Text size="xs" c="dimmed">Call recording (5-min presigned URL; refresh to renew).</Text>
          <audio ref={audioRef} controls src={recordingUrl} style={{ width: '100%' }}>
            Your browser does not support audio playback.
          </audio>
        </Stack>
      ) : (
        <Alert color="yellow" variant="light" mb="sm">
          Recording URL unavailable. Transcript still rendered below.
        </Alert>
      )}

      {transcriptChunks.length > 0 ? (
        <>
          <Text size="sm" fw={600} mb={4}>
            Transcript ({transcriptChunks.length} chunks) — click a row to scrub
          </Text>
          <ScrollArea h={280} type="auto" offsetScrollbars>
            <Stack gap={2}>
              {transcriptChunks.map((c) => (
                <Group
                  key={c.sequence}
                  gap="xs"
                  wrap="nowrap"
                  align="flex-start"
                  onClick={() => seekTo(c)}
                  style={{
                    cursor: typeof c.startMs === 'number' ? 'pointer' : 'default',
                    padding: '4px 8px',
                    borderRadius: 4,
                    background: 'var(--mantine-color-default-hover)'
                  }}
                >
                  <Text size="xs" c="dimmed" style={{ minWidth: 48, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtTime(c.startMs)}
                  </Text>
                  <Badge size="xs" color={speakerColor(c.speaker)} variant="light" style={{ minWidth: 76 }}>
                    {c.speaker ?? '—'}
                  </Badge>
                  <Text size="sm" style={{ flex: 1 }}>{c.text}</Text>
                  {typeof c.startMs === 'number' && (
                    <Tooltip label="Scrub to this moment">
                      <ActionIcon size="xs" variant="subtle" color="gray">
                        <IconPlayerPlay size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              ))}
            </Stack>
          </ScrollArea>
        </>
      ) : (
        <Text size="sm" c="dimmed">No transcript chunks persisted for this run.</Text>
      )}

      {aiSummary && (
        <Alert color="violet" variant="light" mt="md" title="AI diagnosis">
          <Text size="sm">{aiSummary}</Text>
          <Text size="xs" c="dimmed" mt={4}>
            Generated by Bedrock Haiku at finalize time. Treat as a starting point — confirm against the recording above.
          </Text>
        </Alert>
      )}
    </Card>
  );
}

export default ReportViewer;
